import { createContext, useContext, useMemo, useState } from 'react';
import { translations } from './translations';

const LANG_KEY = 'sp_lang';
const I18nContext = createContext(null);

function getByPath(obj, path) {
  return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
}

function interpolate(template, vars = {}) {
  if (typeof template !== 'string') return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => String(vars[key] ?? ''));
}

function getInitialLanguage() {
  if (typeof window === 'undefined') return 'en';
  const stored = window.localStorage.getItem(LANG_KEY);
  return stored === 'ar' || stored === 'en' ? stored : 'en';
}

export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(getInitialLanguage);

  const setLanguage = (lang) => {
    const next = lang === 'ar' ? 'ar' : 'en';
    setLanguageState(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANG_KEY, next);
    }
  };

  const value = useMemo(() => {
    const t = (key, vars) => {
      const dict = translations[language] || translations.en;
      const fallback = translations.en;
      const raw = getByPath(dict, key) ?? getByPath(fallback, key) ?? key;
      return interpolate(raw, vars);
    };

    return {
      language,
      setLanguage,
      isArabic: language === 'ar',
      t,
    };
  }, [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
}
